import React, { useState, useEffect } from 'react';

import {
  Typography, Box, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, IconButton, Card, CardContent,
  Accordion, AccordionSummary, AccordionDetails, Avatar, Divider, Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, ExpandMore as ExpandMoreIcon,
  Person as PersonIcon, Build as BuildIcon, ContentCut as SewingIcon,
  Brush as UpholsteryIcon, LocalShipping as DriverIcon, Store as SellerIcon,
  AdminPanelSettings as AdminIcon, SupervisorAccount as ManagerIcon,
  Work as WorkIcon, AttachMoney as MoneyIcon, Telegram as TelegramIcon,
  Delete as DeleteIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { getAllUsers, createUser, updateUser, deleteUser } from '../api';

const ROLE_ORDER = ['sewer', 'upholsterer', 'frame_worker', 'driver', 'seller', 'manager', 'admin'];

const ROLE_CONFIG = {
  admin: { label: 'Администраторы', singularLabel: 'Администратор', icon: AdminIcon, color: '#374151', bgColor: '#f3f4f6' },
  manager: { label: 'Управляющие', singularLabel: 'Управляющая', icon: ManagerIcon, color: '#1e40af', bgColor: '#eff6ff' },
  frame_worker: { label: 'Каркасники', singularLabel: 'Каркасник', icon: BuildIcon, color: '#475569', bgColor: '#f1f5f9' },
  sewer: { label: 'Швеи', singularLabel: 'Швея', icon: SewingIcon, color: '#64748b', bgColor: '#f8fafc' },
  upholsterer: { label: 'Обстрельщики', singularLabel: 'Обстрельщик', icon: UpholsteryIcon, color: '#334155', bgColor: '#f1f5f9' },
  driver: { label: 'Водители', singularLabel: 'Водитель', icon: DriverIcon, color: '#6b7280', bgColor: '#f3f4f6' },
  seller: { label: 'Продавцы', singularLabel: 'Продавец', icon: SellerIcon, color: '#4b5563', bgColor: '#f9fafb' },
};

function WorkerCard({ worker, onEdit, onDelete, isAdmin }) {
  const roleConfig = ROLE_CONFIG[worker.role] || { color: '#757575', bgColor: '#f5f5f5', singularLabel: worker.role };
  const RoleIcon = roleConfig.icon || PersonIcon;
  const hasCurrentWork = worker.current_sofa && worker.current_sofa_model;
  
  return (
    <Card sx={{ mb: 2, borderLeft: '4px solid ' + roleConfig.color, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 3 } }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="center" gap={2} flex={1}>
            <Avatar sx={{ bgcolor: roleConfig.bgColor, color: roleConfig.color, width: 48, height: 48 }}>
              <RoleIcon />
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>{worker.full_name}</Typography>
              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                <Chip label={roleConfig.singularLabel} size="small" sx={{ bgcolor: roleConfig.bgColor, color: roleConfig.color, fontWeight: 500 }} />
                {hasCurrentWork && (
                  <Tooltip title="Текущий диван в работе">
                    <Chip icon={<WorkIcon sx={{ fontSize: 16 }} />} label={worker.current_sofa_model + ' (№' + worker.current_sofa + ')'} size="small" color="primary" variant="outlined" />
                  </Tooltip>
                )}
                {worker.today_earnings > 0 && (
                  <Tooltip title="Заработок за сегодня">
                    <Chip icon={<MoneyIcon sx={{ fontSize: 16 }} />} label={worker.today_earnings.toLocaleString() + ' ₸'} size="small" color="success" variant="outlined" />
                  </Tooltip>
                )}
                {!hasCurrentWork && ['sewer', 'upholsterer', 'frame_worker'].includes(worker.role) && (
                  <Chip label="Свободен" size="small" variant="outlined" sx={{ color: '#9e9e9e', borderColor: '#e0e0e0' }} />
                )}
                {worker.telegram_id ? (
                  <Tooltip title={`Telegram привязан (ID: ${worker.telegram_id})`}>
                    <Chip icon={<TelegramIcon sx={{ fontSize: 14 }} />} label="TG" size="small" color="info" variant="outlined" />
                  </Tooltip>
                ) : (
                  ['sewer', 'upholsterer', 'frame_worker'].includes(worker.role) && (
                    <Tooltip title="Telegram не привязан — уведомления не приходят">
                      <Chip icon={<TelegramIcon sx={{ fontSize: 14 }} />} label="Нет TG" size="small" variant="outlined" sx={{ color: '#ef4444', borderColor: '#fca5a5' }} />
                    </Tooltip>
                  )
                )}
              </Box>
            </Box>
          </Box>
          {isAdmin && (
            <Box display="flex" flexDirection="column" gap={0.5}>
              <IconButton size="small" onClick={function() { onEdit(worker); }} color="primary">
                <EditIcon fontSize="small" />
              </IconButton>
              {worker.role !== 'admin' && (
                <IconButton size="small" onClick={function() { onDelete(worker); }} sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function RoleGroup({ role, workers, onEdit, onDelete, isAdmin, defaultExpanded }) {
  const roleConfig = ROLE_CONFIG[role] || { label: role, color: '#757575', bgColor: '#f5f5f5', icon: PersonIcon };
  const RoleIcon = roleConfig.icon || PersonIcon;
  const workingCount = workers.filter(function(w) { return w.current_sofa; }).length;
  
  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ mb: 2, '&:before': { display: 'none' }, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: roleConfig.bgColor, borderBottom: '2px solid ' + roleConfig.color, '&:hover': { bgcolor: roleConfig.bgColor } }}>
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <Avatar sx={{ bgcolor: roleConfig.color, color: 'white', width: 36, height: 36 }}>
            <RoleIcon fontSize="small" />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, color: roleConfig.color }}>{roleConfig.label}</Typography>
          <Box display="flex" gap={1} ml="auto" mr={2}>
            <Chip label={workers.length + ' чел.'} size="small" sx={{ bgcolor: 'white', fontWeight: 500 }} />
            {workingCount > 0 && <Chip icon={<WorkIcon sx={{ fontSize: 14 }} />} label={workingCount + ' в работе'} size="small" color="primary" />}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, bgcolor: '#fafafa' }}>
        {workers.length === 0 ? (
          <Typography color="text.secondary" align="center" py={2}>Нет сотрудников в этой категории</Typography>
        ) : (
          workers.map(function(worker) { return <WorkerCard key={worker.id} worker={worker} onEdit={onEdit} onDelete={onDelete} isAdmin={isAdmin} />; })
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default function WorkersList({ user }) {
  const [workers, setWorkers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentWorker, setCurrentWorker] = useState(null);
  const [formData, setFormData] = useState({ username: '', password: '', full_name: '', role: 'sewer', telegram_id: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [workerToDelete, setWorkerToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(function() { loadWorkers(); }, []);

  function loadWorkers() {
    getAllUsers().then(function(response) {
      setWorkers(response.data);
    }).catch(function(error) {
      console.error('Error loading workers:', error);
    });
  }

  function handleOpenDialog(worker) {
    if (worker) {
      setEditMode(true);
      setCurrentWorker(worker);
      setFormData({ username: worker.username, password: '', full_name: worker.full_name, role: worker.role, telegram_id: worker.telegram_id || '' });
    } else {
      setEditMode(false);
      setCurrentWorker(null);
      setFormData({ username: '', password: '', full_name: '', role: 'sewer', telegram_id: '' });
    }
    setOpenDialog(true);
  }

  function handleCloseDialog() {
    setOpenDialog(false);
    setEditMode(false);
    setCurrentWorker(null);
  }

  function handleSave() {
    var data = Object.assign({}, formData);
    if (editMode && !data.password) {
      delete data.password;
    }
    // Handle telegram_id: send as number or null
    if (data.telegram_id) {
      data.telegram_id = parseInt(data.telegram_id, 10) || null;
    } else {
      delete data.telegram_id;
    }
    var promise = editMode ? updateUser(currentWorker.id, data) : createUser(data);
    promise.then(function() {
      handleCloseDialog();
      loadWorkers();
    }).catch(function(error) {
      console.error('Error saving worker:', error);
      var msg = error.response && error.response.data && error.response.data.detail ? error.response.data.detail : error.message;
      alert('Ошибка сохранения: ' + msg);
    });
  }

  function handleDeleteClick(worker) {
    setWorkerToDelete(worker);
    setDeleteDialog(true);
  }

  function handleDeleteConfirm() {
    if (!workerToDelete) return;
    setDeleteLoading(true);
    deleteUser(workerToDelete.id).then(function(response) {
      setDeleteDialog(false);
      setWorkerToDelete(null);
      loadWorkers();
    }).catch(function(error) {
      var msg = error.response && error.response.data && error.response.data.detail ? error.response.data.detail : error.message;
      alert('Ошибка удаления: ' + msg);
    }).finally(function() {
      setDeleteLoading(false);
    });
  }

  var groupedWorkers = {};
  ROLE_ORDER.forEach(function(role) {
    groupedWorkers[role] = workers.filter(function(w) { return w.role === role; });
  });
  
  var isAdmin = user.role === 'admin';
  var totalWorkers = workers.length;
  var workingNow = workers.filter(function(w) { return w.current_sofa; }).length;
  var todayEarnings = workers.reduce(function(sum, w) { return sum + (w.today_earnings || 0); }, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Сотрудники</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Chip icon={<PersonIcon />} label={'Всего: ' + totalWorkers} color="default" />
            <Chip icon={<WorkIcon />} label={'В работе: ' + workingNow} color="primary" />
            <Chip icon={<MoneyIcon />} label={'Заработано сегодня: ' + todayEarnings.toLocaleString() + ' ₸'} color="success" />
          </Box>
        </Box>
        {isAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={function() { handleOpenDialog(); }} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}>
            Добавить сотрудника
          </Button>
        )}
      </Box>

      {ROLE_ORDER.map(function(role) {
        var roleWorkers = groupedWorkers[role] || [];
        if (roleWorkers.length === 0 && ['sewer', 'upholsterer', 'frame_worker'].indexOf(role) === -1) {
          return null;
        }
        return <RoleGroup key={role} role={role} workers={roleWorkers} onEdit={handleOpenDialog} onDelete={handleDeleteClick} isAdmin={isAdmin} defaultExpanded={['sewer', 'upholsterer', 'frame_worker'].indexOf(role) !== -1} />;
      })}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ pb: 1 }}>{editMode ? 'Редактировать сотрудника' : 'Добавить сотрудника'}</DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField fullWidth label="ФИО" value={formData.full_name} onChange={function(e) { setFormData(Object.assign({}, formData, { full_name: e.target.value })); }} required placeholder="Иванов Иван Иванович" />
            <TextField fullWidth label="Логин" value={formData.username} onChange={function(e) { setFormData(Object.assign({}, formData, { username: e.target.value })); }} required disabled={editMode} placeholder="ivanov" helperText={editMode ? "Логин нельзя изменить" : "Латинские буквы и цифры"} />
            <TextField fullWidth label={editMode ? 'Новый пароль (оставьте пустым)' : 'Пароль'} type={showPassword ? 'text' : 'password'} value={formData.password} onChange={function(e) { setFormData(Object.assign({}, formData, { password: e.target.value })); }} required={!editMode} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={function() { setShowPassword(!showPassword); }} edge="end" size="small">{showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></InputAdornment> }} />
            {editMode && (
              <TextField fullWidth label="Telegram ID" value={formData.telegram_id} onChange={function(e) { setFormData(Object.assign({}, formData, { telegram_id: e.target.value })); }} placeholder="Например: 123456789" helperText={formData.telegram_id ? '✅ Привязан — уведомления будут приходить' : '⚠️ Не привязан — сотрудник может привязать через бота командой /link'} type="number" />
            )}
            <TextField fullWidth select label="Роль" value={formData.role} onChange={function(e) { setFormData(Object.assign({}, formData, { role: e.target.value })); }} required>
              <MenuItem value="sewer"><Box display="flex" alignItems="center" gap={1}><SewingIcon sx={{ color: ROLE_CONFIG.sewer.color }} />Швея</Box></MenuItem>
              <MenuItem value="upholsterer"><Box display="flex" alignItems="center" gap={1}><UpholsteryIcon sx={{ color: ROLE_CONFIG.upholsterer.color }} />Обстрельщик</Box></MenuItem>
              <MenuItem value="frame_worker"><Box display="flex" alignItems="center" gap={1}><BuildIcon sx={{ color: ROLE_CONFIG.frame_worker.color }} />Каркасник</Box></MenuItem>
              <MenuItem value="driver"><Box display="flex" alignItems="center" gap={1}><DriverIcon sx={{ color: ROLE_CONFIG.driver.color }} />Водитель</Box></MenuItem>
              <MenuItem value="seller"><Box display="flex" alignItems="center" gap={1}><SellerIcon sx={{ color: ROLE_CONFIG.seller.color }} />Продавец</Box></MenuItem>
              <MenuItem value="manager"><Box display="flex" alignItems="center" gap={1}><ManagerIcon sx={{ color: ROLE_CONFIG.manager.color }} />Управляющая</Box></MenuItem>
              <MenuItem value="admin"><Box display="flex" alignItems="center" gap={1}><AdminIcon sx={{ color: ROLE_CONFIG.admin.color }} />Администратор</Box></MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>Отмена</Button>
          <Button onClick={handleSave} variant="contained" color="primary" sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}>{editMode ? 'Сохранить' : 'Создать'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={function() { setDeleteDialog(false); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ color: '#dc2626' }}>Удалить сотрудника?</DialogTitle>
        <DialogContent>
          {workerToDelete && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Вы уверены, что хотите удалить сотрудника <b>{workerToDelete.full_name}</b>?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Все активные задачи сотрудника будут сняты и переведены в статус «Ожидание». История выполненных задач сохранится.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={function() { setDeleteDialog(false); }} sx={{ textTransform: 'none' }}>Отмена</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={deleteLoading} sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}>
            {deleteLoading ? 'Удаление...' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
